import {
  METRICS_INTERVAL_CONFIG,
  METRICS_SERIES,
  METRICS_TABS,
  MetricsInterval,
  MetricsTabId,
} from './constants';
import { MetricsChartData } from './types';

interface MetricsDataPoint {
  timestamp: number;
  error: number;
  retries: number;
  start: number;
  success: number;
}

const MINUTE = 60 * 1000;
const BASE_STEP_MS = 5 * MINUTE;
const BASE_DURATION_MS = 26 * 7 * 24 * 60 * MINUTE;

const TAB_SCALE: Record<MetricsTabId, number> = {
  sagemaker: 1,
  queue: 1.4,
  scoring: 0.8,
  database: 0.6,
  sentry: 0.35,
};

const SERIES_CACHE_VERSION = `${BASE_DURATION_MS}-${BASE_STEP_MS}-v5`;

const seriesCache: Partial<
  Record<
    MetricsTabId,
    { origin: number; version: string; points: MetricsDataPoint[] }
  >
> = {};

const mulberry32 = (seed: number) => {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const seedFrom = (tabId: MetricsTabId) => {
  let hash = 0;
  for (let i = 0; i < tabId.length; i += 1) {
    hash = (hash << 5) - hash + tabId.charCodeAt(i);
    hash |= 0;
  }
  return hash || 1;
};

const average = (values: number[]) =>
  values.length
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : 0;

const generateBaseSeries = (
  tabId: MetricsTabId,
  origin: number
): MetricsDataPoint[] => {
  const random = mulberry32(seedFrom(tabId));
  const scale = TAB_SCALE[tabId];
  const pointCount = Math.floor(BASE_DURATION_MS / BASE_STEP_MS) + 1;
  const startAt = origin - (pointCount - 1) * BASE_STEP_MS;

  return Array.from({ length: pointCount }, (_, i) => {
    const timestamp = startAt + i * BASE_STEP_MS;
    const progress = i / Math.max(pointCount - 1, 1);
    const wobble = Math.sin(progress * Math.PI * 8);

    return {
      timestamp,
      error: Math.max(0, Math.round((3 + wobble * 2 + random() * 3) * scale)),
      retries: Math.max(
        0,
        Math.round((14 + wobble * 4 + random() * 5) * scale)
      ),
      start: Math.max(0, Math.round((22 + wobble * 5 + random() * 6) * scale)),
      success: Math.max(0, Math.round((9 + wobble * 3 + random() * 4) * scale)),
    };
  });
};

const getBaseSeries = (tabId: MetricsTabId): MetricsDataPoint[] => {
  const cached = seriesCache[tabId];
  if (!cached || cached.version !== SERIES_CACHE_VERSION) {
    const origin = Date.now();
    seriesCache[tabId] = {
      origin,
      version: SERIES_CACHE_VERSION,
      points: generateBaseSeries(tabId, origin),
    };
  }

  return seriesCache[tabId]!.points;
};

const bucketPoints = (
  points: MetricsDataPoint[],
  bucketMs: number
): MetricsDataPoint[] => {
  if (!points.length) {
    return [];
  }

  const buckets = new Map<number, MetricsDataPoint[]>();

  points.forEach((point) => {
    const key = Math.floor(point.timestamp / bucketMs) * bucketMs;
    const bucket = buckets.get(key) || [];
    bucket.push(point);
    buckets.set(key, bucket);
  });

  return Array.from(buckets.entries())
    .sort(([left], [right]) => left - right)
    .map(([timestamp, bucket]) => ({
      timestamp,
      error: Math.round(average(bucket.map((point) => point.error))),
      retries: Math.round(average(bucket.map((point) => point.retries))),
      start: Math.round(average(bucket.map((point) => point.start))),
      success: Math.round(average(bucket.map((point) => point.success))),
    }));
};

export const getMockMetricsGraph = (
  tabId: MetricsTabId,
  interval: MetricsInterval
): MetricsChartData => {
  const { lookbackMs, periodMs } = METRICS_INTERVAL_CONFIG[interval];
  const tab = METRICS_TABS.find((item) => item.id === tabId);
  const series = getBaseSeries(tabId);
  const end = series[series.length - 1]?.timestamp || Date.now();
  const windowStart = end - lookbackMs;
  const windowPoints = series.filter((point) => point.timestamp >= windowStart);
  const points = bucketPoints(windowPoints, periodMs);

  return {
    title: `${tab?.label || 'Metrics'} counters over (${interval})`,
    interval,
    timestamps: points.map((point) => point.timestamp),
    series: METRICS_SERIES.map((item) => {
      const values = points.map((point) => point[item.id]);
      const total = values.reduce((sum, value) => sum + value, 0);

      return {
        key: item.id,
        name: item.name,
        color: item.color,
        values,
        stats: {
          mean: values.length ? total / values.length : 0,
          last: values[values.length - 1] ?? 0,
          max: values.length ? Math.max(...values) : 0,
          total,
        },
      };
    }),
  };
};
